import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

interface ManifestFile {
  name: string;
  path: string;
}

interface AllManifestsModalProps {
  show: boolean;
  onClose: () => void;
  manifestFiles: ManifestFile[];
  onDelete: (fileName: string) => void;
  apiBaseUrl: string;
}

const AllManifestsModal: React.FC<AllManifestsModalProps> = ({
  show,
  onClose,
  manifestFiles,
  onDelete,
  apiBaseUrl
}) => {
  if (!show) {
    return null;
  }

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose}></div>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">All Manifest Files</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
            </div>
            <div className="modal-body">
              {manifestFiles.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Filename</th>
                        <th>Type</th>
                        <th>Model</th>
                        <th>Hardware</th>
                        <th>Version</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {manifestFiles.map((file, index) => {
                        // Determine if it's a factory manifest from filename
                        const isFactory = file.name.startsWith('factory-');
                        
                        // Extract model name from filename pattern
                        let modelMatch;
                        if (isFactory) {
                          modelMatch = file.name.match(/factory-(.+?)-v/);
                        } else {
                          modelMatch = file.name.match(/^(.+?)-v/);
                        }
                        const model = modelMatch ? modelMatch[1].replace(/_/g, ' ').split('-')[0].trim() : 'Unknown';
                        
                        // Extract version from filename pattern 
                        const versionMatch = file.name.match(/[-_]v(\d+\.\d+\.\d+)\.json$/);
                        const version = versionMatch ? versionMatch[1] : 'Unknown';
                        
                        // Extract hardware revision (if exists)
                        const hardwareMatch = file.name.match(/rev(\d+)/i);
                        const hardware = hardwareMatch ? `Rev ${hardwareMatch[1]}` : '-';
                        
                        return (
                          <tr key={index}>
                            <td>{file.name}</td>
                            <td>
                              <span className={`badge ${isFactory ? 'bg-warning text-dark' : 'bg-success'}`}>
                                {isFactory ? 'Factory' : 'Release'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${
                                model.includes('Fluora Mini') 
                                  ? 'bg-primary' 
                                  : model.includes('Fluora Floor') 
                                    ? 'bg-info'
                                    : model.includes('Monos')
                                      ? 'bg-warning text-dark'
                                      : 'bg-warning text-dark'
                              }`}>
                                {model}
                              </span>
                            </td>
                            <td>{hardware}</td>
                            <td>v{version}</td>
                            <td>
                              <a 
                                href={`${apiBaseUrl}${file.path}`} 
                                className="btn btn-sm btn-outline-primary me-2"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open"
                              >
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                              </a>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                  onDelete(file.name);
                                  // Close modal if last file is deleted
                                  if (manifestFiles.length === 1) {
                                    onClose();
                                  }
                                }}
                                title="Delete"
                              >
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-3">
                  <p className="text-muted mb-0">No manifest files available.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AllManifestsModal; 